// Story library data layer.
//
// Two tiers of laziness:
//   1. The story index manifest (~250 KB at 1.4k stories) lives at
//      /storyIndex.json as a static asset and is fetched once on the
//      first call to loadStoryIndex(). This keeps it OUT of the JS
//      bundle entirely — the home screen and other non-library
//      surfaces no longer pay for it on first paint.
//   2. Per-story JSON (~3-5 KB each) lives at /stories/<slug>.json
//      and is fetched on demand by getStory(slug), cached for the
//      session.
//
// Per kernel principle 9 ("persistent artifact"), completion state is
// persisted to the active child's progress so the library grows over
// time and never resets.
//
// MA-189 — the index fetch carries a `?v=<build sha>` query param so
// vercel.json can cache /storyIndex.json aggressively without a
// stale-content window: a new deploy always requests a new URL, so
// there's nothing to revalidate. Vercel's `source` header match is on
// the pathname only (querystring-agnostic), so the caching rule still
// applies. Per-story JSON at /stories/<slug>.json is NOT versioned this
// way — it stays on a short max-age + stale-while-revalidate window
// (see vercel.json) since content-hashing every one of ~1.4k per-story
// files isn't worth the ingest-pipeline change for a bounded ~1hr
// staleness window.
//
// indexVersion() reads import.meta.env directly rather than reusing
// qaCaptureHooks.ts's buildShaAttr() (same literal, same "dev"
// fallback): this module is imported by node/tsx test harnesses outside
// Vite, where `import.meta.env` itself is undefined (not just the var
// on it) — a bare property read on it throws. Guard defensively instead
// of coupling a caching concern to the live-QA capture-hook module.
function indexVersion(): string {
  const env = (import.meta as unknown as { env?: Record<string, string | undefined> }).env;
  const sha = env?.VITE_VERCEL_GIT_COMMIT_SHA;
  return typeof sha === "string" && sha.length > 0 ? sha : "dev";
}

export type ReadingLevel = "A" | "B" | "C" | "D";

/** MA-566 — per-page illustration provenance. "human" = the page's picture is
 *  the work of the book's credited illustrator (or its source); "ai" = the
 *  picture was generated by PencilWise's AI image lane. Optional on every
 *  page: absent means the book-level truth (resolvePageIllustrationOrigin).
 *  A page AI art fills inside a book credited to a human illustrator MUST
 *  carry "ai" (scripts/content/illustrationOrigin.mjs enforces it against
 *  the story image manifest), so the credits card can keep the human credit
 *  and say which pages are not theirs. */
export type IllustrationOrigin = "human" | "ai";

interface StoryPage {
  text: string;
  imagePath: string | null;
  illustrationOrigin?: IllustrationOrigin;
}

/** MA-566 — a page's illustration origin: its explicit `illustrationOrigin`
 *  when valid, otherwise the book-level truth — "ai" for an AI-authored story
 *  (`origin: "ai_generated"`, whose pictures all come from the AI lane),
 *  "human" for every other book. */
export function resolvePageIllustrationOrigin(
  story: { origin?: string },
  page: { illustrationOrigin?: unknown } | null | undefined,
): IllustrationOrigin {
  const explicit = page?.illustrationOrigin;
  if (explicit === "human" || explicit === "ai") return explicit;
  return story.origin === "ai_generated" ? "ai" : "human";
}

/** MA-774 — a cover's illustration origin: the story's explicit
 *  `coverIllustrationOrigin` when valid, otherwise the book-level truth (same
 *  rule as resolvePageIllustrationOrigin). A cover AI fills inside a book
 *  credited to a human illustrator MUST carry "ai" so the credits card shows
 *  the approved AI-illustration line (scripts/content/illustrationOrigin.mjs
 *  enforces it against the story image manifest). */
export function resolveCoverIllustrationOrigin(
  story: { origin?: string; coverIllustrationOrigin?: unknown } | null | undefined,
): IllustrationOrigin {
  const explicit = story?.coverIllustrationOrigin;
  if (explicit === "human" || explicit === "ai") return explicit;
  return story?.origin === "ai_generated" ? "ai" : "human";
}

export interface StoryAttribution {
  license: string;
  text: string | null;
  illustration: string | null;
  translation: string | null;
  other: string[];
  sourceRepo: string;
  sourceFile: string;
  imageRepo: string | null;
  imageLicense: string | null;
  rightsHolder: string;
}

/** MA-170 — additive curation metadata. Computed by
 *  scripts/content/curationTiers.mjs from a deterministic funnel (dup-body
 *  dedup, non-English, worksheet/adult-literacy artifacts, test-book,
 *  overlong, CRLF titles, level-formula misfits). Missing on an entry ==
 *  "ship" (both scripts/audit/storiesAudit.mjs and the picker filters
 *  below default an absent tier to servable) — this is metadata, not a
 *  gate that fails closed on its own absence. */
export type CurationTier = "ship" | "fix" | "never";

/** MA-253 — additive art-coverage metadata. Computed by
 *  scripts/content/artCoverage.mjs from the real on-disk `pages[].imagePath`
 *  resolution: "full" = every page resolves to a real file, "partial" = some
 *  but not all, "none" = zero (incl. a book with no pages at all). Missing on
 *  an entry == not-yet-classified — treated as "none" by every consumer
 *  (`storyCatalog.ts`'s `illustratedFirst`, the MA-180 picker), never as a
 *  fail-open "full". */
export type ArtCoverage = "full" | "partial" | "none";

/** MA-172 — additive subject-taxonomy metadata. Computed by
 *  scripts/content/subjectTaxonomy.mjs for ship-tier books only (a fix/never
 *  tier book never carries this field). 1-2 slugs from the fixed taxonomy
 *  declared in that script (SUBJECTS) — no free-form values. */
export type SubjectSlug =
  | "animals"
  | "folk-tales"
  | "science-nature"
  | "funny"
  | "counting"
  | "feelings-friends"
  | "family-home"
  | "adventure"
  | "school-learning"
  | "everyday-life";

interface Story {
  slug: string;
  title: string;
  pageCount: number;
  wordCount: number;
  level: ReadingLevel;
  pages: StoryPage[];
  coverPath: string | null;
  /** MA-774 — provenance of the cover picture (see resolveCoverIllustrationOrigin). */
  coverIllustrationOrigin?: IllustrationOrigin;
  attribution: StoryAttribution;
  curationTier?: CurationTier;
  needsReviewFlags?: string[];
  subjects?: SubjectSlug[];
  artCoverage?: ArtCoverage;
  /** ⭐MA-481 — set on the NON-canonical twin of a duplicated story, naming the
   *  slug kept on the browse shelf. Generated by
   *  scripts/content/duplicateCanonical.mjs (`--check`-enforced); consumed by
   *  `canonicalOnly`. Absent on every canonical and on every unique story. */
  duplicateOf?: string;
  /** MA-191 — provenance for AI-authored stories (scripts/content/aiStoryGate.mjs
   *  requires both non-empty when origin is "ai_generated"; storiesAudit.mjs
   *  enforces the same at the served-corpus level). Absent == human-authored. */
  origin?: "ai_generated";
  reviewedBy?: string;
  reviewedAt?: string;
  /** MA-191 — the declared PHONICS_SCOPE ids (scripts/content/aiStoryGate.mjs)
   *  an AI-authored story's decodability was validated against. Consumed only
   *  by that gate (plain JSON read, no TS import) — typed here for schema
   *  documentation/completeness on the `Story` shape. Absent on every
   *  non-AI-authored story. */
  taughtPatterns?: string[];
  /** MA-191/MA-261 — the R0-R5 cumulative-letter phase this story clears,
   *  computed by src/curriculum/decodabilityEngine.js's auditStory() (via
   *  scripts/content/aiStoryIngest.mjs for AI stories, or the frozen
   *  scripts/content/readingPhaseRelevel.mjs CSV ingest for the legacy
   *  corpus). "none" == clears no phase. */
  highestCumulativePhasePassed?: string;
  phaseDecodablePct?: number;
  beginnerVisible?: boolean;
}

export interface StorySummary {
  slug: string;
  title: string;
  pageCount: number;
  wordCount: number;
  level: ReadingLevel;
  coverPath: string | null;
  curationTier?: CurationTier;
  needsReviewFlags?: string[];
  subjects?: SubjectSlug[];
  artCoverage?: ArtCoverage;
  /** ⭐MA-481 — set on the NON-canonical twin of a duplicated story, naming the
   *  slug kept on the browse shelf. Generated by
   *  scripts/content/duplicateCanonical.mjs (`--check`-enforced); consumed by
   *  `canonicalOnly`. Absent on every canonical and on every unique story. */
  duplicateOf?: string;
  /** MA-173 — the text-author credit (attribution.text), carried into the
   *  index so cover tiles can show "by {author}" without fetching the full
   *  per-story JSON. Absent/omitted when the story has no text-author credit
   *  (the "rightsHolder only" case — the tile shows nothing in that case;
   *  the full credit still appears on the book's end-of-story credits page). */
  author?: string;
  /** MA-191 — carried from the per-story JSON's `origin` field so the picker
   *  can apply the ≤1-AI-of-3 cap without fetching the full story. Absent ==
   *  human-authored (no "human" value is ever written — see aiStoryGate.mjs). */
  origin?: "ai_generated";
  /** MA-260 — the MA-261/MA-191 crosswalk fields, present on every index
   *  entry since the relevel ingest (rebuildIndexFromDisk carries them) but
   *  previously untyped on this summary shape. The serve-time phase gate
   *  (src/lib/engine/readingPhaseGate.ts) reads `highestCumulativePhasePassed`
   *  ("none" or "r1".."r5", lowercase per the CSV ingest) from the INDEX so
   *  gating never needs a per-story fetch. */
  highestCumulativePhasePassed?: string;
  phaseDecodablePct?: number;
  beginnerVisible?: boolean;
  /** MA-235 — carried from the per-story JSON's `taughtPatterns` (same
   *  PHONICS_SCOPE ids as the `Story` shape above) so the day-end picker's
   *  decodable sub-band frontier selector (src/lib/engine/decodableFrontier.ts)
   *  can grade a book without fetching its full page content. Absent on
   *  every story that hasn't declared a phonics scope. */
  taughtPatterns?: string[];
}

interface IndexFile {
  source: string;
  license: string;
  rightsHolder: string;
  fetchedAt: string;
  count: number;
  stories: StorySummary[];
}

// Index manifest: fetched once, then cached for the session. The promise
// is itself cached so concurrent callers share one network round-trip.
let INDEX_CACHE: IndexFile | null = null;
let INDEX_INFLIGHT: Promise<IndexFile> | null = null;

async function fetchIndex(): Promise<IndexFile> {
  if (INDEX_CACHE) return INDEX_CACHE;
  if (INDEX_INFLIGHT) return INDEX_INFLIGHT;
  INDEX_INFLIGHT = (async () => {
    try {
      const res = await fetch(`/storyIndex.json?v=${encodeURIComponent(indexVersion())}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = (await res.json()) as IndexFile;
      INDEX_CACHE = data;
      return data;
    } finally {
      INDEX_INFLIGHT = null;
    }
  })();
  return INDEX_INFLIGHT;
}

// Session cache so re-opening a story doesn't re-fetch.
const STORY_CACHE = new Map<string, Story>();
const INFLIGHT = new Map<string, Promise<Story | null>>();

/**
 * All story summaries in catalog order. Async because the manifest is
 * fetched lazily on first access (kept out of the bundle to protect
 * first-paint cost as the library grows past 30 stories).
 */
export async function loadStoryIndex(): Promise<StorySummary[]> {
  const idx = await fetchIndex();
  return idx.stories.slice();
}

/**
 * Synchronous accessor for callers that already triggered a load and
 * just want the cached value. Returns null if the index hasn't been
 * fetched yet — callers must handle the loading state themselves.
 */
export function peekStoryIndex(): StorySummary[] | null {
  return INDEX_CACHE ? INDEX_CACHE.stories.slice() : null;
}

/**
 * Fetch a single story by slug. Returns null if not in the manifest, the
 * slug is unknown (404), or the network fetch fails/parses malformed.
 * Cached for the session.
 *
 * MA-190 — fetches `/stories/<slug>.json` directly instead of gating on
 * `fetchIndex()` first. The old index-membership pre-check made every
 * deep-link story open a serial 2-hop wait (265KB index, THEN the ~3-5KB
 * story) even though index membership was never a security boundary (no
 * earned gate exists on /play/story; all story JSONs are already publicly
 * deployed) — it just delayed the fetch that actually matters. An unknown
 * slug now costs one 404 round-trip instead of a free short-circuit, same
 * null return either way.
 */
export async function getStory(slug: string): Promise<Story | null> {
  if (!slug) return null;
  const cached = STORY_CACHE.get(slug);
  if (cached) return cached;
  const inflight = INFLIGHT.get(slug);
  if (inflight) return inflight;

  const p = (async () => {
    try {
      const res = await fetch(`/stories/${encodeURIComponent(slug)}.json`);
      if (!res.ok) return null;
      const story = (await res.json()) as Story;
      // MA-167 — a malformed story JSON (pages missing/non-array) must
      // degrade to the reader's existing ReaderFallback, never crash on
      // `story.pages.length` in the component.
      if (!Array.isArray(story?.pages)) return null;
      STORY_CACHE.set(slug, story);
      return story;
    } catch {
      return null;
    } finally {
      INFLIGHT.delete(slug);
    }
  })();
  INFLIGHT.set(slug, p);
  return p;
}

/**
 * MA-190 — the image path (if any) the reader should warm the browser
 * cache for while page `page` is on screen, so the tap to page+1 paints
 * from cache instead of popping in ~2s later. Pure so it's unit-testable
 * without a DOM: null past the last real page (the virtual credits page
 * has no image), null when the next page carries no image, null when
 * the next page's image already 404'd once (brokenPageIndexes) — no
 * point re-requesting a known-broken asset.
 */
export function nextPrefetchImagePath(
  pages: StoryPage[],
  page: number,
  brokenPageIndexes?: Set<number>,
): string | null {
  const nextIndex = page + 1;
  if (nextIndex >= pages.length) return null;
  if (brokenPageIndexes?.has(nextIndex)) return null;
  return pages[nextIndex]?.imagePath ?? null;
}

// ── Completion state ──────────────────────────────────────────
// Stored in progress.completedStories: string[]. The App owns the
// progress object; these helpers are pure and side-effect-free so
// callers stay in control of persistence.

export function isStoryCompleted(progress: any, slug: string): boolean {
  const list = progress?.completedStories;
  return Array.isArray(list) && list.includes(slug);
}

export function markStoryCompleted(progress: any, slug: string): any {
  const existing: string[] = Array.isArray(progress?.completedStories) ? progress.completedStories : [];
  if (existing.includes(slug)) return progress;
  return { ...progress, completedStories: [...existing, slug] };
}

/** MA-174 — the completedStories-grandfather clause needs the raw slug
 *  list (as a Set) to check membership; this is the same list-extraction
 *  pattern as isStoryCompleted above. */
export function completedStorySlugs(progress: any): string[] {
  const list = progress?.completedStories;
  return Array.isArray(list) ? list : [];
}
